
// import {
//   Component,
//   ChangeDetectionStrategy,
//   Input,
//   Output,
//   EventEmitter,
//   ViewChild,
//   OnChanges,
//   SimpleChanges
// } from '@angular/core';
// import { AgGridAngular } from 'ag-grid-angular';
// import {
//   ColDef,
//   GridApi,
//   GridOptions,
//   GridReadyEvent,
//   IDetailCellRendererParams
// } from 'ag-grid-community';
// import { DetailCellRendererComponent } from './detail-cell-renderer/detail-cell-renderer.component';

// @Component({
//   selector: 'app-hierarchical-grid',
//   templateUrl: './hierarchical-grid.component.html',
//   styleUrls: ['./hierarchical-grid.component.scss'],
//   changeDetection: ChangeDetectionStrategy.OnPush
// })
// export class HierarchicalGridComponent implements OnChanges {
//   @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

//   // ---------- Inputs ----------
//   @Input() parentColumnDefs: ColDef[] = [];
//   @Input() childColumnDefs: ColDef[] = [];
//   @Input() rowData: any[] = [];
//   @Input() childDataProperty?: string;
//   @Input() getChildData?: (parent: any) => any[] | Promise<any[]>;
//   @Input() enableAddChildRow = false;
//   @Input() addChildRowLabel = 'Add Child Row';
//   @Input() masterGridOptions: GridOptions = {};

//   // ---------- Outputs ----------
//   @Output() addChildRow = new EventEmitter<any>();

//   // Master grid API
//   public gridApi!: GridApi;
//   public gridOptions: GridOptions = {};

//   // Detail renderer reference
//   public detailCellRenderer = DetailCellRendererComponent;

//   ngOnInit(): void {
//     this.buildGridOptions();
//   }

//   ngOnChanges(changes: SimpleChanges): void {
//     // When critical inputs change, rebuild the grid options and update the grid if already initialised
//     const criticalKeys = ['parentColumnDefs', 'childColumnDefs', 'rowData', 'enableAddChildRow'];
//     if (criticalKeys.some(key => changes[key])) {
//       this.buildGridOptions();
//       if (this.gridApi) {
//         // Update master grid with new options
//         this.gridApi.setGridOption('columnDefs', this.parentColumnDefs);
//         this.gridApi.setGridOption('rowData', this.rowData);
//         this.gridApi.setGridOption('detailCellRendererParams', this.getDetailParams());
//       }
//     }
//   }

//   private buildGridOptions(): void {
//     this.gridOptions = {
//       columnDefs: this.parentColumnDefs,
//       rowData: this.rowData,
//       masterDetail: true,
//       detailCellRenderer: this.detailCellRenderer,
//       detailCellRendererParams: this.getDetailParams(),
//       detailRowHeight: 280,
//       onGridReady: (event: GridReadyEvent) => {
//         this.gridApi = event.api;
//       },
//       ...this.masterGridOptions,
//     };
//   }

//   private getDetailParams(): IDetailCellRendererParams {
//     return {
//       childColumnDefs: this.childColumnDefs,
//       childDataProperty: this.childDataProperty,
//       getChildData: this.getChildData,
//       enableAddChildRow: this.enableAddChildRow,
//       addChildRowLabel: this.addChildRowLabel,
//       onAddChild: (parent: any) => this.addChildRow.emit(parent),
//     } as any; // cast to any because IDetailCellRendererParams doesn't include our custom properties
//   }

//   /**
//    * Call this after you’ve updated parent rows’ child data.
//    * It forces the master grid to re‑render, which automatically refreshes all detail grids.
//    */
//   public refreshMasterGrid(): void {
//     this.gridApi?.refreshClientSideRowModel();
//   }
// }

import {
  Component, ChangeDetectionStrategy, Input, Output, EventEmitter,
  ViewChild, OnChanges, SimpleChanges
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { AddChildRendererComponent } from './add-child-renderer/add-child-renderer.component';

@Component({
  selector: 'app-hierarchical-grid',
  templateUrl: './hierarchical-grid.component.html',
  styleUrls: ['./hierarchical-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HierarchicalGridComponent implements OnChanges {
  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  @Input() columnDefs: ColDef[] = [];
  @Input() rowData: any[] = [];
  @Input() childDataProperty = 'children';        // optional, only if you need it elsewhere
  @Input() enableAddChildRow = false;
  @Input() addChildRowLabel = 'Add Child';

  // Still allow parents to pass external custom renderers if needed
  @Input() frameworkComponents: any = {};

  @Output() addChildRow = new EventEmitter<any>();

  public gridApi!: GridApi;
  public gridOptions: GridOptions = {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columnDefs'] || changes['rowData'] || changes['enableAddChildRow']) {
      this.buildGridOptions();
      if (this.gridApi) {
        // Update grid with new options
        this.gridApi.setGridOption('columnDefs', this.prepareColumnDefs());
        this.gridApi.setGridOption('rowData', this.rowData);
      }
    }
  }

  private buildGridOptions(): void {
    this.gridOptions = {
      columnDefs: this.prepareColumnDefs(),
      rowData: this.rowData,
      treeData: true,
      getDataPath: (data: any) => data.orgHierarchy,
      groupDefaultExpanded: 0,
      components: this.getMergedComponents(),
      onGridReady: (event: GridReadyEvent) => this.gridApi = event.api,
    };
  }

  /**
   * If enableAddChildRow is true, append the "Add Child" column.
   */
  private prepareColumnDefs(): ColDef[] {
    const baseCols = [...this.columnDefs];
    if (this.enableAddChildRow) {
      const addColumn: ColDef = {
        headerName: '',
        width: 130,
        cellRenderer: 'addChildRenderer',   // internal renderer name
        cellRendererParams: {
          addChildRowLabel: this.addChildRowLabel,
          onAddChild: (parent: any) => this.addChildRow.emit(parent)
        },
        // Hide the button on child rows (rows that don't have the "children" property)
        cellClassRules: {
          'd-none': (params) => !params.data?.children
        }
      };
      baseCols.unshift(addColumn);
    }
    return baseCols;
  }

  /**
   * Merge our internal renderer with any external ones the parent may have provided.
   */
  private getMergedComponents(): any {
    const components: any = { ...this.frameworkComponents };
    if (this.enableAddChildRow) {
      components['addChildRenderer'] = AddChildRendererComponent;
    }
    return components;
  }

  public refreshGrid(): void {
    this.gridApi?.refreshClientSideRowModel();
  }
}